import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { Header } from '../../Components/Header/Header';
import { ProductMappingAutoMapDialog } from '../../Components/Vending/ProductMappingAutoMapDialog';
import {
  MapStatusChip,
  MappingStatusFilter,
  MappingTypeTabs,
  mappingActionLinkSx,
  mappingCellText,
  mappingToolbarSx,
} from '../../Components/Vending/MappingShared';
import { compactDialogTableSx } from '../../Components/Vending/VendingStyles';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  AimsCatalogProduct,
  ProductCatalogSearchResponse,
  ProductMappingListResponse,
  ProductMappingRow,
  ProductMappingSuggestion,
  VendingMachineListResponse,
  VendingMachineSummary,
} from '../../Types/vending';

type MapStatusFilter = 'unmapped' | 'mapped' | 'conflict' | 'all';

const SEARCH_DEBOUNCE_MS = 350;

const aimsProductLabel = (product: AimsCatalogProduct | null) => {
  if (!product) return '—';
  const code = product.product_code || product.sku || `PID ${product.pid}`;
  return `${code} — ${product.name || 'Unnamed'}`;
};

const machineLabel = (machine: VendingMachineSummary) =>
  machine.machine_name || `VM-${machine.provider_machine_id}`;

export const VendingProductMappingPage = () => {
  const [machines, setMachines] = useState<VendingMachineSummary[]>([]);
  const [machineFilter, setMachineFilter] = useState('');
  const [rows, setRows] = useState<ProductMappingRow[]>([]);
  const [stats, setStats] = useState({ unmapped: 0, mapped: 0, conflict: 0 });
  const [loading, setLoading] = useState(false);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<MapStatusFilter>('unmapped');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoMapOpen, setAutoMapOpen] = useState(false);
  const [selected, setSelected] = useState<ProductMappingRow | null>(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogResults, setCatalogResults] = useState<AimsCatalogProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedPid, setSelectedPid] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const loadMachines = async () => {
      setMachinesLoading(true);
      try {
        const response = await vendingApiClient('/machines?limit=500');
        const json: VendingMachineListResponse = await response.json();
        setMachines(json.data ?? []);
      } catch {
        setMachines([]);
      } finally {
        setMachinesLoading(false);
      }
    };
    loadMachines();
  }, []);

  const selectedMachine = useMemo(
    () => machines.find((m) => String(m.id) === machineFilter) ?? null,
    [machines, machineFilter],
  );

  const loadMappings = useCallback(async () => {
    if (!machineFilter) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: '200',
        map_status: statusFilter,
        machine_id: machineFilter,
      });
      if (debouncedSearch) params.set('q', debouncedSearch);
      const response = await vendingApiClient(`/mappings/products?${params.toString()}`);
      const json: ProductMappingListResponse = await response.json();
      setRows(json.data);
      setStats({
        unmapped: json.meta?.stats?.unmapped ?? 0,
        mapped: json.meta?.stats?.mapped ?? 0,
        conflict: json.meta?.stats?.conflict ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product mappings');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, machineFilter, statusFilter]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  useEffect(() => {
    if (!dialogOpen || catalogSearch.trim().length < 2) {
      setCatalogResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setCatalogLoading(true);
      try {
        const params = new URLSearchParams({ q: catalogSearch.trim() });
        const response = await vendingApiClient(`/mappings/products/catalog-search?${params.toString()}`);
        const json: ProductCatalogSearchResponse = await response.json();
        setCatalogResults(json.data);
      } catch {
        setCatalogResults([]);
      } finally {
        setCatalogLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [catalogSearch, dialogOpen]);

  const openMapDialog = (row: ProductMappingRow, suggestion?: ProductMappingSuggestion) => {
    setSelected(row);
    setSelectedPid(suggestion?.pid ?? row.pid ?? null);
    setCatalogSearch(
      suggestion?.product_code
      || suggestion?.sku
      || row.provider_item_id
      || row.provider_sku
      || row.name
      || '',
    );
    setCatalogResults([]);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
    setSelectedPid(null);
    setCatalogSearch('');
    setCatalogResults([]);
  };

  const submitMapping = async (body: Record<string, unknown>) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await vendingApiClient(`/mappings/products/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      closeDialog();
      setSuccess('Product mapping saved.');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAction = useCallback(async (row: ProductMappingRow, body: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await vendingApiClient(`/mappings/products/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setSuccess('Product mapping updated.');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  }, [loadMappings]);

  const handleAutoMapApplied = async (mappedCount: number) => {
    setSuccess(`Auto-mapped ${mappedCount} exact match${mappedCount === 1 ? '' : 'es'}.`);
    await loadMappings();
  };

  const columns: ColumnDef<ProductMappingRow>[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Vendor Product',
      width: 200,
      searchable: true,
      valueGetter: (_v, row) => row.name || '—',
      renderCell: (params) => (
        <Typography sx={{ ...mappingCellText, fontWeight: 600 }}>
          {params.row.name || '—'}
        </Typography>
      ),
    },
    {
      field: 'provider_item_id',
      headerName: 'Item ID',
      width: 110,
      valueGetter: (_v, row) => row.provider_item_id || '—',
      renderCell: (params) => (
        <Typography sx={mappingCellText}>{params.row.provider_item_id || '—'}</Typography>
      ),
    },
    {
      field: 'provider_sku',
      headerName: 'SKU',
      width: 100,
      valueGetter: (_v, row) => row.provider_sku || '—',
      renderCell: (params) => (
        <Typography sx={mappingCellText}>{params.row.provider_sku || '—'}</Typography>
      ),
    },
    {
      field: 'map_status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => <MapStatusChip status={params.row.map_status ?? params.value} />,
    },
    {
      field: 'aims_product',
      headerName: 'AIMS Product',
      width: 260,
      valueGetter: (_v, row) => aimsProductLabel(row.aims_product),
      renderCell: (params) => (
        <Typography sx={mappingCellText} title={aimsProductLabel(params.row.aims_product)}>
          {aimsProductLabel(params.row.aims_product)}
        </Typography>
      ),
    },
    {
      field: 'id',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const suggestion = params.row.suggestions?.[0];
        return (
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
            {params.row.map_status !== 'mapped' && (
              <Typography
                component="button"
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  openMapDialog(params.row);
                }}
                sx={mappingActionLinkSx}
              >
                Map
              </Typography>
            )}
            {suggestion && params.row.map_status !== 'mapped' && (
              <Typography
                component="button"
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  openMapDialog(params.row, suggestion);
                }}
                sx={mappingActionLinkSx}
              >
                Suggest
              </Typography>
            )}
            {(params.row.map_status === 'mapped' || params.row.map_status === 'conflict') && (
              <Typography
                component="button"
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuickAction(params.row, { action: 'unmap' });
                }}
                sx={mappingActionLinkSx}
              >
                Unmap
              </Typography>
            )}
          </Box>
        );
      },
    },
  ], [handleQuickAction, saving]);

  return (
    <PageContainer>
      <Header
        title="Mappings"
        subtitle={
          selectedMachine
            ? `${machineLabel(selectedMachine)} · Unmapped ${stats.unmapped} · Mapped ${stats.mapped} · Conflict ${stats.conflict}`
            : 'Select a machine to map products placed on it'
        }
        count={machineFilter ? rows.length : undefined}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={() => setAutoMapOpen(true)}
            disabled={loading || saving || !machineFilter}
            sx={{
              backgroundColor: COLORS.teal,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: COLORS.darkTeal, boxShadow: 'none' },
            }}
          >
            Auto-map exact matches
          </Button>
          <Button
            variant="outlined"
            onClick={loadMappings}
            disabled={loading || saving || !machineFilter}
            sx={{
              borderColor: COLORS.teal,
              color: COLORS.teal,
              textTransform: 'none',
              '&:hover': { borderColor: COLORS.darkTeal, backgroundColor: `${COLORS.teal}0a` },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Header>

      <MappingTypeTabs active="products" refreshKey={success} />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={mappingToolbarSx}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="product-mapping-machine-label">Machine</InputLabel>
              <Select
                labelId="product-mapping-machine-label"
                label="Machine"
                value={machineFilter}
                onChange={(e) => setMachineFilter(e.target.value)}
                disabled={machinesLoading}
              >
                <MenuItem value="">
                  <em>Select a machine…</em>
                </MenuItem>
                {machines.map((machine) => (
                  <MenuItem key={machine.id} value={String(machine.id)}>
                    {machineLabel(machine)}
                    {machine.location_name ? ` · ${machine.location_name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search"
              placeholder="Vendor name, item ID, SKU…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disabled={!machineFilter}
              sx={{ minWidth: 260, maxWidth: 420 }}
            />
          </Box>

          {machineFilter && (
            <MappingStatusFilter<MapStatusFilter>
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: `All (${stats.unmapped + stats.mapped + stats.conflict})` },
                { value: 'unmapped', label: `Unmapped (${stats.unmapped})` },
                { value: 'mapped', label: `Mapped (${stats.mapped})` },
                { value: 'conflict', label: `Conflict (${stats.conflict})` },
              ]}
            />
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!machineFilter && !machinesLoading && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Choose a machine first — only products placed on that machine are shown for mapping.
            </Alert>
          )}
        </Box>

        {machinesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!machinesLoading && machineFilter && loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!machinesLoading && machineFilter && !loading && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataTable
              columns={columns}
              data={rows.map((r) => ({ ...r, id: r.id }))}
              pageKey={`vending-product-mapping-${machineFilter}-${statusFilter}`}
              idField="id"
              loading={loading}
              rowHeight={48}
            />
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Map to AIMS product</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{selected.name || '—'}</strong>
                {' · '}
                {selected.provider_item_id || '—'}
                {selected.provider_sku ? ` · ${selected.provider_sku}` : ''}
              </Typography>

              <TextField
                size="small"
                label="Search AIMS catalogue"
                placeholder="SKU, product code, or name…"
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                fullWidth
              />

              {catalogLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                  <CircularProgress size={22} sx={{ color: COLORS.teal }} />
                </Box>
              )}

              {!catalogLoading && catalogResults.length > 0 && (
                <TableContainer sx={{ maxHeight: 260, border: `1px solid ${COLORS.lightGray}40`, borderRadius: 1 }}>
                  <Table stickyHeader size="small" sx={compactDialogTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product code</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {catalogResults.map((product) => (
                        <TableRow
                          key={product.pid}
                          hover
                          selected={selectedPid === product.pid}
                          onClick={() => setSelectedPid(product.pid)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ fontWeight: selectedPid === product.pid ? 600 : 400 }}>
                            {product.product_code || '—'}
                          </TableCell>
                          <TableCell>{product.sku || '—'}</TableCell>
                          <TableCell>{product.name || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {selectedPid && (
                <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                  Selected: {aimsProductLabel(
                    catalogResults.find((p) => p.pid === selectedPid)
                    ?? selected.suggestions.find((s) => s.pid === selectedPid)
                    ?? null,
                  )}
                </Typography>
              )}

              {selected.mapped_at && (
                <Typography variant="caption" color="text.secondary">
                  Last mapped {formatUTC(selected.mapped_at)} UTC
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} size="small" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => selectedPid && submitMapping({ pid: selectedPid })}
            disabled={saving || !selectedPid}
            sx={{
              backgroundColor: COLORS.teal,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: COLORS.darkTeal, boxShadow: 'none' },
            }}
          >
            {saving ? 'Saving…' : 'Save mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      <ProductMappingAutoMapDialog
        open={autoMapOpen}
        machineId={machineFilter}
        machineLabel={selectedMachine ? machineLabel(selectedMachine) : undefined}
        onClose={() => setAutoMapOpen(false)}
        onApplied={handleAutoMapApplied}
      />
    </PageContainer>
  );
};

export default VendingProductMappingPage;
